import * as yup from 'yup'
import Lazy from 'yup/lib/Lazy'
import { isEmpty } from 'lodash'
import { isPhoneNumberValid } from './phoneValidation'
type Schema = Lazy<any, unknown> | any

/**
 * A set of common validations.
 * Don't add to this class unless a given validation is needed more than once.
 * For one-time validations use the "custom" method of ValidationSchemaBuilder
 */
export const registeredBusinessStructure = {
  noBusinessOptionId: '926845ea-b6c7-4bc0-b32d-2c85a0e8e36j',
  tellMeMoreOptionId: 'c7986148-1f62-465d-967b-636fd6053subQ31',
}

export const registeredBusinessStructureV2 = {
  noBusinessOptionId: '5c163600-88ce-4746-abec-4a26f7f1f45e',
  otherBusinessOptionId: 'ea85986c-e37a-498a-817f-2c6152d1a25e',
}

export const isValid = (registerBusinessStructureOptionId, value) => {
  if (
    !isEmpty(registerBusinessStructureOptionId) &&
    registerBusinessStructureOptionId !== registeredBusinessStructure.noBusinessOptionId &&
    registerBusinessStructureOptionId !== registeredBusinessStructure.tellMeMoreOptionId
  ) {
    return !value ? false : true
  } else {
    return true
  }
}

export abstract class CommonValidations {
  abstract firstName()
  abstract lastName()
  abstract email()
  abstract mobileContactNumber()
  abstract ein()
  abstract yearsInBusiness()
  abstract businessName()
}

/** Responsible for defining validation rules with the "yup" library */
export class ValidationsProvider {
  firstName() {
    return yup.string().trim().required('Please enter first name')
  }

  lastName() {
    return yup.string().trim().required('Please enter last name')
  }

  email() {
    return yup
      .string()
      .email('Please enter a valid email address')
      .required('Please enter a valid email address')
  }
  isSmsOptedIn() {
    return yup.boolean().oneOf([true], 'Agreement is required to subscribe.')
  }
  password() {
    return yup.string().required('Please enter a password')
  }

  businessEmail() {
    return yup
      .string()
      .email('Please enter a valid Business Email')
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .nullable()
  }
  businessWebsite() {
    return yup
      .string()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .matches(
        /^(https?:\/\/)?([\w\d-]+\.)+[\w-]+(\/[\w- ;,./?%&=]*)?$/i,
        'Please enter a valid Business Website'
      )
      .nullable()
  }
  businessEINCode() {
    return yup
      .string()
      .trim()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('hasEINOrNot', 'Enter a valid ein number', function (value) {
        const { registerBusinessStructureOptionId, hasRegisterBusinessStructure, hasEIN } =
          this.parent
        if (
          (registerBusinessStructureOptionId &&
            registerBusinessStructureOptionId ===
              registeredBusinessStructureV2.noBusinessOptionId) ||
          !hasRegisterBusinessStructure ||
          !hasEIN
        ) {
          return true
        }
        return yup
          .string()
          .matches(/^[0-9]{2}-[0-9]{7}$/i, 'Please enter a valid EIN number')
          .required('Please enter a valid ein number')
          .isValidSync(value)
      })
      .nullable()
  }
  domainName() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-url-or-none', 'Please enter a valid Business Website', function (value) {
        const { hasWebsite } = this.parent
        if (!hasWebsite) {
          return true
        }
        return yup
          .string()
          .matches(
            /^(https?:\/\/)?([\w\d-]+\.)+[\w-]+(\/[\w- ;,./?%&=]*)?$/i,
            'Please enter a valid Business Website'
          )
          .required('Please Eenter Website URL')
          .isValidSync(value)
      })
      .nullable()
  }
  month() {
    return yup
      .string()
      .test('checkYearMonthError', 'Enter month ', function (value) {
        const { registerBusinessStructureOptionId } = this.parent

        return isValid(registerBusinessStructureOptionId, value)
      })
      .nullable()
  }
  year() {
    return yup
      .string()
      .test('checkYearMonthError', 'Enter year ', function (value) {
        const { registerBusinessStructureOptionId } = this.parent
        return isValid(registerBusinessStructureOptionId, value)
      })
      .nullable()
  }
  mobileContactNumber() {
    return yup
      .string()
      .required('Phone number is required')
      .test('isValidPhoneNumber', 'Please enter a valid mobile phone number', isPhoneNumberValid)
  }

  workPhoneNumber() {
    return yup
      .string()
      .test('isValidPhoneNumber', 'Please enter a valid work phone number', isPhoneNumberValid)
  }

  ein() {
    return yup
      .string()
      .matches(/^[0-9]{2}-[0-9]{7}$/i, 'Please enter a valid EIN number')
      .required('Please enter a valid ein number')
  }
  naicsCode() {
    return yup
      .string()
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Please enter a valid NAICS number')
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .nullable()
  }

  yearsInBusiness() {
    return yup
      .string()
      .matches(/^(?!00)[0-9]{1,4}$/i, 'Year must be between 0 to 1000')
      .required('You must enter year')
  }

  einCode() {
    return yup
      .string()
      .matches(/^[0-9]{2}-[0-9]{7}$/i, 'Please enter a valid EIN number')
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .nullable()
  }

  businessName() {
    return yup.string().nullable()
  }
  businessStage() {
    return yup.string().trim().required('Select business stage')
  }
  registeredBusinessStructure() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .trim()
      .test('has-business-structure-or-not', 'Please select Business Structure', function (value) {
        const { hasRegisterBusinessStructure } = this.parent
        if (!hasRegisterBusinessStructure) {
          return true
        }
        return yup.string().required('Please select Business Structure').isValidSync(value)
      })
      .nullable()
  }
  otherRegisteredBusinessStructure() {
    return yup
      .string()
      .trim()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test(
        'checkOtherRegisteredBusinessStructure',
        'Enter other business structure',
        function (value) {
          const { registerBusinessStructureOptionId } = this.parent
          if (
            registerBusinessStructureOptionId ===
            registeredBusinessStructureV2.otherBusinessOptionId
          ) {
            return yup.string().required('Enter other business structure').isValidSync(value)
          }
          return true
        }
      )
      .nullable()
  }
  clientDetailsPartTimeEmployees() {
    return yup
      .string()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
      .nullable()
  }
  clientDetailsFullTimeEmployees() {
    return yup
      .string()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
      .nullable()
  }

  totalEmployees() {
    return yup
      .string()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
      .nullable()
  }

  clientDetailsContractors() {
    return yup
      .string()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
      .nullable()
  }
  clientDetailsVolunteers() {
    return yup
      .string()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? null : value))
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
      .nullable()
  }
  fullTimeEmployees() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'must be a number')
  }
  partTimeEmployees() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'must be a number')
  }
  contractors() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'must be a number')
  }
  volunteers() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'must be a number')
  }
  jobsCreated() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
  }
  revenue() {
    return yup
      .string()
      .required('Please enter 0 or dollars and cents (eg. $15.50)')
      .matches(
        /^([-]?(?!00)[0-9*$,+]*(\.[0-9*$,+]+)?)$/i,
        'Please enter 0 or dollars and cents (eg. $15.50)'
      )
  }
  wages() {
    return yup
      .string()
      .required('Please enter 0 or dollars and cents (eg. $15.50)')
      .matches(
        /^(?!00)[0-9*$,+]*(\.[0-9*$,+]+)?$/i,
        'Please enter 0 or dollars and cents (eg. $15.50)'
      )
  }
  debtFinancing() {
    return yup
      .string()
      .required('Please enter 0 or dollars and cents (eg. $15.50)')
      .matches(
        /^(?!00)[0-9*$,+]*(\.[0-9*$,+]+)?$/i,
        'Please enter 0 or dollars and cents (eg. $15.50)'
      )
  }
  businessBankAccount() {
    return yup
      .string()
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
      .required('Must be a number')
  }
  newCustomer() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
  }
  productSale() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
  }
  serviceSale() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
  }
  targetAudience() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
  }
  physicalLocation() {
    return yup
      .string()
      .required('Must be a number')
      .matches(/^(?!00)[0-9,]{1,}$/i, 'Must be a number')
  }
  financialGrowth() {
    return yup.string().required('Choose profit or loss for last month').nullable()
  }
  businessAddress() {
    return yup.object().shape({
      addressLine1: yup
        .string()
        .transform((value, originalValue) => (value === null ? '' : value))
        .test('required-address-line1', 'Business Address is required', function (value) {
          this.parent.hasBusinessAddress = this.parent?.hasBusinessAddress === false ? false : true
          const { hasBusinessAddress } = this.parent
          if (hasBusinessAddress && !value) {
            return false
          }
          return true
        }),
    })
  }
  url() {
    return yup.string().url('Please enter a valid URL').required('URL is required')
  }
  facebook() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-facebook-or-none', 'Please enter a valid Facebook link', function (value) {
        const { hasFacebook } = this.parent
        if (!hasFacebook) {
          return true
        }
        return yup.string().required('Please enter a Facebook link').isValidSync(value)
      })
      .nullable()
  }
  instagram() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-instagram-or-none', 'Please enter a valid Instagram link', function (value) {
        const { hasInstagram } = this.parent
        if (!hasInstagram) {
          return true
        }
        return yup.string().required('Please enter an Instagram link').isValidSync(value)
      })
      .nullable()
  }
  linkedin() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-linkedin-or-none', 'Please enter a valid LinkedIn link', function (value) {
        const { hasLinkedin } = this.parent
        if (!hasLinkedin) {
          return true
        }
        return yup.string().required('Please enter a LinkedIn link').isValidSync(value)
      })
      .nullable()
  }
  tiktok() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-tiktok-or-none', 'Please enter a valid TikTok link', function (value) {
        const { hasTikTok } = this.parent
        if (!hasTikTok) {
          return true
        }
        return yup
          .string()
          .matches(/^@[\w.]+$/, 'Please enter a valid TikTok link')
          .required('Please enter a TikTok link')
          .isValidSync(value)
      })
      .nullable()
  }
  twitter() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-twitter-or-none', 'Please enter a valid Twitter link', function (value) {
        const { hasTwitter } = this.parent
        if (!hasTwitter) {
          return true
        }
        return yup.string().required('Please enter a Twitter link').isValidSync(value)
      })
      .nullable()
  }
  youtube() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-youtube-or-none', 'Please enter a valid YouTube link', function (value) {
        const { hasYoutube } = this.parent
        if (!hasYoutube) {
          return true
        }
        return yup
          .string()
          .matches(/^@[\w.]+$/, 'Please enter a valid YouTube link')
          .required('Please enter a YouTube link')
          .isValidSync(value)
      })
      .nullable()
  }
  hasEIN() {
    return yup
      .boolean()
      .transform((value, originalValue) => (value === null || value === '' ? false : value))
      .required()
  }

  hasRegisterBusinessStructure() {
    return yup
      .boolean()
      .transform((value, originalValue) => (value === null || value === '' ? false : value))
      .required()
  }
  hasWebsite() {
    return yup
      .boolean()
      .transform((value, originalValue) => (value === null || value === '' ? false : value))
      .required()
  }
  hasBoolean() {
    return yup
      .boolean()
      .transform((value, originalValue) => (value === null ? true : value))
      .required()
  }
  yearEstablished() {
    return yup
      .string()
      .transform((value, originalValue) => (value === null ? '' : value))
      .test('valid-yearEstablished-or-none', 'Please enter valid date', function (value) {
        const { registerBusinessStructureOptionId, hasRegisterBusinessStructure } = this.parent
        if (
          (registerBusinessStructureOptionId &&
            registerBusinessStructureOptionId ===
              registeredBusinessStructureV2.noBusinessOptionId) ||
          !hasRegisterBusinessStructure
        ) {
          return true
        }
        return yup
          .string()
          .matches(/^(0[1-9]|1[0-2])\/\d{4}$/, 'Must be in the format MM/YYYY')
          .test('is-valid-date', 'Date must not be in the future', (value) => {
            if (!value) return true
            const [month, year] = value.split('/').map(Number)
            const currentDate = new Date()
            const inputDate = new Date(year, month - 1)

            return inputDate <= currentDate
          })
          .required('Please enter Date')
          .isValidSync(value)
      })
      .test('yearEstablished', 'Registration must not be in the future.', function (value) {
        const { yearEstablished } = this.parent
        const { isAfter } = require('date-fns')
        const currentDate = new Date()
        const inputDate = new Date(yearEstablished)
        if (isAfter(inputDate, currentDate)) {
          return false
        } else {
          return true
        }
      })
      .nullable()
  }
}

/** Builder class for creating yup schemas */
class ValidationSchemaBuilder extends CommonValidations {
  provider = new ValidationsProvider()
  validations: { [key: string]: any } = {}
  _schema: Schema | null = null

  updateSchema() {
    this._schema = yup.object(this.validations) as Schema
  }

  /** Returns the finished schema and clears itself to make another schema */
  getSchema() {
    const schema = this._schema
    this.validations = {}
    this._schema = null
    return schema as Schema
  }

  /** Add custom, one-off properties to the schema */
  custom(obj: { [key: string]: any }) {
    Object.assign(this.validations, obj)
    this.updateSchema()
    return this
  }

  firstName() {
    this.validations.firstName = this.provider.firstName()
    this.updateSchema()
    return this
  }

  lastName() {
    this.validations.lastName = this.provider.lastName()
    this.updateSchema()
    return this
  }

  password() {
    this.validations.password = this.provider.password()
    this.updateSchema()
    return this
  }

  email() {
    this.validations.email = this.provider.email()
    this.updateSchema()
    return this
  }
  mobileContactNumber() {
    this.validations.mobileContactNumber = this.provider.mobileContactNumber()
    this.updateSchema()
    return this
  }

  workPhoneNumber() {
    this.validations.workPhoneNumber = this.provider.workPhoneNumber()
    this.updateSchema()
    return this
  }
  isSmsOptedIn() {
    this.validations.isSmsOptedIn = this.provider.isSmsOptedIn()
    this.updateSchema()
    return this
  }

  ein() {
    this.validations.EIN = this.provider.einCode()
    this.updateSchema()
    return this
  }
  naicsCode() {
    this.validations.naicsCode = this.provider.naicsCode()
    this.updateSchema()
    return this
  }

  yearsInBusiness() {
    this.validations.yearsInBusiness = this.provider.yearsInBusiness()
    this.updateSchema()
    return this
  }

  businessName() {
    this.validations.businessName = this.provider.businessName()
    this.updateSchema()
    return this
  }
  businessStage() {
    this.validations.businessStageOptionId = this.provider.businessStage()
    this.updateSchema()
    return this
  }
  registeredBusinessStructure() {
    this.validations.registerBusinessStructureOptionId = this.provider.registeredBusinessStructure()
    this.updateSchema()
    return this
  }
  otherRegisteredBusinessStructure() {
    this.validations.otherRegisteredBusinessStructure =
      this.provider.otherRegisteredBusinessStructure()
    this.updateSchema()
    return this
  }
  fullTimeEmployees() {
    this.validations.fullTimeEmployees = this.provider.fullTimeEmployees()
    this.updateSchema()
    return this
  }
  partTimeEmployees() {
    this.validations.partTimeEmployees = this.provider.partTimeEmployees()
    this.updateSchema()
    return this
  }
  contractors() {
    this.validations.contractors = this.provider.contractors()
    this.updateSchema()
    return this
  }
  volunteers() {
    this.validations.volunteers = this.provider.volunteers()
    this.updateSchema()
    return this
  }

  businessEmail() {
    this.validations.businessEmail = this.provider.businessEmail()
    this.updateSchema()
    return this
  }
  businessWebsite() {
    this.validations.domainName = this.provider.businessWebsite()
    this.updateSchema()
    return this
  }

  clientDetailsPartTimeEmployees() {
    this.validations.partTimeEmployees = this.provider.clientDetailsPartTimeEmployees()
    this.updateSchema()
    return this
  }
  clientDetailsFullTimeEmployees() {
    this.validations.fullTimeEmployees = this.provider.clientDetailsFullTimeEmployees()
    this.updateSchema()
    return this
  }
  totalEmployees() {
    this.validations.totalEmployees = this.provider.totalEmployees()
    this.updateSchema()
    return this
  }
  clientDetailsContractors() {
    this.validations.contractors = this.provider.clientDetailsContractors()
    this.updateSchema()
    return this
  }
  clientDetailsVolunteers() {
    this.validations.volunteers = this.provider.clientDetailsVolunteers()
    this.updateSchema()
    return this
  }

  month() {
    this.validations.month = this.provider.month()
    this.updateSchema()
    return this
  }
  year() {
    this.validations.year = this.provider.year()
    this.updateSchema()
    return this
  }
  jobsCreated() {
    this.validations.jobsCreated = this.provider.jobsCreated()
    this.updateSchema()
    return this
  }
  revenue() {
    this.validations.revenue = this.provider.revenue()
    this.updateSchema()
    return this
  }
  wages() {
    this.validations.wages = this.provider.wages()
    this.updateSchema()
    return this
  }
  debtFinancing() {
    this.validations.debtFinancing = this.provider.debtFinancing()
    this.updateSchema()
    return this
  }
  businessBankAccount() {
    this.validations.businessBankAccount = this.provider.businessBankAccount()
    this.updateSchema()
    return this
  }
  newCustomer() {
    this.validations.newCustomer = this.provider.newCustomer()
    this.updateSchema()
    return this
  }
  productSale() {
    this.validations.productSale = this.provider.productSale()
    this.updateSchema()
    return this
  }
  serviceSale() {
    this.validations.serviceSale = this.provider.serviceSale()
    this.updateSchema()
    return this
  }
  targetAudience() {
    this.validations.targetAudience = this.provider.targetAudience()
    this.updateSchema()
    return this
  }
  physicalLocation() {
    this.validations.physicalLocation = this.provider.physicalLocation()
    this.updateSchema()
    return this
  }
  financialGrowth() {
    this.validations.financialGrowth = this.provider.financialGrowth()
    this.updateSchema()
    return this
  }
  businessAddress() {
    this.validations.businessAddress = this.provider.businessAddress()
    this.updateSchema()
    return this
  }

  url() {
    this.validations.url = this.provider.url()
    this.updateSchema()
    return this
  }
  facebook() {
    this.validations.facebook = this.provider.facebook()
    this.updateSchema()
    return this
  }
  instagram() {
    this.validations.instagram = this.provider.instagram()
    this.updateSchema()
    return this
  }
  linkedin() {
    this.validations.linkedin = this.provider.linkedin()
    this.updateSchema()
    return this
  }
  tiktok() {
    this.validations.tiktok = this.provider.tiktok()
    this.updateSchema()
    return this
  }
  twitter() {
    this.validations.twitter = this.provider.twitter()
    this.updateSchema()
    return this
  }
  youtube() {
    this.validations.youtube = this.provider.youtube()
    this.updateSchema()
    return this
  }
  hasEIN() {
    this.validations.hasEIN = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasRegisterBusinessStructure() {
    this.validations.hasRegisterBusinessStructure = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasWebsite() {
    this.validations.hasWebsite = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasFacebook() {
    this.validations.hasFacebook = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasInstagram() {
    this.validations.hasInstagram = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasTikTok() {
    this.validations.hasTikTok = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasLinkedin() {
    this.validations.hasLinkedin = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasYoutube() {
    this.validations.hasYoutube = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  hasTwitter() {
    this.validations.hasTwitter = this.provider.hasBoolean()
    this.updateSchema()
    return this
  }
  domainName() {
    this.validations.domainName = this.provider.domainName()
    this.updateSchema()
    return this
  }
  businessEINCode() {
    this.validations.EIN = this.provider.businessEINCode()
    this.updateSchema()
    return this
  }
  yearEstablished() {
    this.validations.yearEstablished = this.provider.yearEstablished()
    this.updateSchema()
    return this
  }
}

export default ValidationSchemaBuilder
